import React, {
  ReactElement, ReactNode, useState,
} from 'react';
import {
  Button, Col, FormGroup, Input, Label, Row, Select, Textarea,
} from 'idsk-react-components';
import './zaznamSekcieItem.scss';
import { SafeParameterSetter } from '../../../../../model/SafeParameterSetter';
import ZaznamSekcie from '../../../../../model/ZaznamSekcie';
import {
  typSekcieNazov,
  ZaznamSekcieTypEnum,
} from '../../../../../model/enums/ZaznamSekcieTypEnum';

interface Props {
  setItemList: (data: ZaznamSekcie[]) => void;
  itemId: number;
  children: ReactNode;
  itemList: Array<ZaznamSekcie>;
  getFieldErrorMessage: (fieldName: string) => string;
  clearFieldErrorMessage: (fieldName: string) => void;
  clearAllFieldErrorMessages: () => void;
}

const ZaznamSekcieItem = ({
  setItemList,
  itemId,
  children,
  itemList = [],
  clearFieldErrorMessage,
  clearAllFieldErrorMessages,
  getFieldErrorMessage,
}: Props): ReactElement => {
  const [zaznamSekcie, setZaznamSekcie] = useState<ZaznamSekcie>(itemList[itemId]);
  const onRemove = (): void => {
    setItemList(itemList.filter((_, index) => index !== itemId));
    if (clearAllFieldErrorMessages) {
      clearAllFieldErrorMessages();
    }
  };

  const updateField: SafeParameterSetter<ZaznamSekcie> = (fieldName, value) => {
    setZaznamSekcie(prevState => ({ ...prevState, [fieldName]: value }));

    const newFormValues = [...itemList];
    newFormValues[itemId] = { ...zaznamSekcie, [fieldName]: value };
    setItemList(newFormValues);

    if (clearFieldErrorMessage) {
      clearFieldErrorMessage(fieldName + itemId);
    }
  };

  return (
    <>
      <div className="zaznam-sekcie-item">
        <FormGroup>
          <Row>
            <Col width="two-thirds">
              <Label htmlFor="typ">Typ judikátu/rozhodnutia</Label>
            </Col>
          </Row>
          <Row>
            <Col width="one-third">
              <Select
                id="typ"
                name="typ"
                width="full"
                value={zaznamSekcie.typ}
                onChange={
                  ({ target }) => {
                    updateField('typ', target.value);
                    clearFieldErrorMessage(`typ${itemId}`);
                  }
                }
                error={getFieldErrorMessage(`typ${itemId}`)}
              >
                <option key="Vyberte" value="" hidden>Vyberte</option>
                {(Object.values(ZaznamSekcieTypEnum) && Object.values(ZaznamSekcieTypEnum)?.length > 0) ? (
                  <>
                    {Object.values(ZaznamSekcieTypEnum)
                      .map((typSekcie) => (
                        <option key={typSekcie} value={typSekcie}>
                          {typSekcieNazov(typSekcie)}
                        </option>
                      ))}
                  </>
                ) : (
                  <option key="emptyOption" value="emptyOption" disabled>
                    Nenašiel sa žiadny záznam.
                  </option>
                )}
              </Select>
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Row>
            <Col width="two-thirds">
              <Label htmlFor="nazov">Názov judikátu/rozhodnutia</Label>
            </Col>
          </Row>
          <Row>
            <Col width="two-thirds">
              <Textarea
                name="nazov"
                value={zaznamSekcie.nazov}
                onChange={({ target }) => updateField('nazov', target.value)}
                error={getFieldErrorMessage(`nazov${itemId}`)}
                maxLength={256}
                rows={3}
                expandHeight
                showRemaining
              />
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Row>
            <Col width="two-thirds">
              <Label htmlFor="url">
                URL judikátu/rozhodnutia<span className="not-required"> (nepovinný údaj)</span>
              </Label>
            </Col>
          </Row>
          <Row>
            <Col width="two-thirds">
              <Input
                key="url"
                type="text"
                maxLength={256}
                value={zaznamSekcie.url}
                onChange={({ target }) => updateField('url', target.value)}
                error={getFieldErrorMessage(`url${itemId}`)}
              />
            </Col>
          </Row>
        </FormGroup>

        {itemList.length > 0 && (
        <Row>
          <Col width="two-thirds">
            <Button
              type="button"
              size="large"
              className="remove-button"
              buttonStyle="negative"
              onClick={onRemove}
            >
              Zmazať judikát/rozhodnutie
            </Button>
          </Col>
        </Row>
        )}
      </div>
      {children && zaznamSekcie.nazov && zaznamSekcie.typ && zaznamSekcie.nazov.trim().length > 0 && <>{children}</>}
    </>
  );
};

export default ZaznamSekcieItem;
